/**
 * EF-UNIFY-CREW-DOCUMENT-UI-ONBOARDING-AND-PORTAL-V1
 * EF-ENHANCE-DOCUMENT-PREVIEW-UX-ADMIN-AND-CREW-V1
 * 
 * Unified Crew Documents Panel Component
 * 
 * This is the SINGLE SOURCE OF TRUTH for crew document UI.
 * Used by both onboarding flow and crew portal.
 * 
 * Features:
 * - Fetches documents from API
 * - Displays all documents in a list
 * - Allows document upload
 * - Allows document deletion (with confirmation)
 * - Shows document status (Pending/Verified/Rejected)
 * - Document preview (click filename to preview)
 */

import React, { useState, useEffect, useCallback } from "react";
import { useI18n } from "../../i18n/I18nProvider";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Upload,
  FileText,
  AlertCircle,
  Loader2,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { CREW_ENDPOINTS } from "../../api/endpoints";
import { DocumentPreviewModal } from "../shared/DocumentPreviewModal";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * CrewDocumentsPanel - Unified document management component
 * 
 * @param {Object} props
 * @param {Array} props.requiredDocuments - Optional list of required document types
 * @param {Function} props.onDocumentsChange - Optional callback when documents change
 * @param {string} props.className - Optional additional CSS classes
 */
export function CrewDocumentsPanel({ 
  requiredDocuments = [], 
  onDocumentsChange,
  className = "" 
}) {
  const { t } = useI18n();
  // State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null });
  const [deleting, setDeleting] = useState(false);
  // EF-ENHANCE-DOCUMENT-PREVIEW-UX-ADMIN-AND-CREW-V1: Preview state
  const [previewDoc, setPreviewDoc] = useState(null);

  // Fetch documents from API
  const fetchDocuments = useCallback(async () => {
    try {
      const token = localStorage.getItem("eventsflo_auth_token");
      if (!token) {
        console.warn("No auth token found");
        return;
      }

      const response = await fetch(`${API_URL}${CREW_ENDPOINTS.getDocuments}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const docs = data.documents || [];
        setDocuments(docs);
        
        // Notify parent of change
        if (onDocumentsChange) {
          onDocumentsChange(docs);
        }
      } else {
        console.warn("Failed to fetch documents:", response.status);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  }, [onDocumentsChange]);

  // Initial fetch
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Handle file upload
  // EF-ADD-UPLOAD-ERROR-LOGGING-AND-DEBUG-V1: Enhanced error handling with detailed messages
  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    console.log("UPLOAD START:", { 
      filename: file.name, 
      size: file.size, 
      type: file.type 
    });
    
    try {
      const token = localStorage.getItem("eventsflo_auth_token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", "general");

      const response = await fetch(`${API_URL}${CREW_ENDPOINTS.uploadDocument}`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        console.log("UPLOAD SUCCESS:", result);
        toast.success(t("crewPortal.uploadSuccessNamed", { filename: file.name }));
        // Always fetch fresh list from API
        await fetchDocuments();
      } else {
        // EF-ADD-UPLOAD-ERROR-LOGGING-AND-DEBUG-V1: Parse and display detailed error
        let errorMessage = t("crewPortal.uploadFailedDefault");
        let errorDetails = null;
        
        try {
          const error = await response.json();
          console.error("UPLOAD ERROR RESPONSE:", error);
          
          // Handle both string and object error details
          if (typeof error.detail === 'string') {
            errorMessage = error.detail;
          } else if (typeof error.detail === 'object') {
            errorMessage = error.detail.message || t("crewPortal.uploadFailedDefault");
            errorDetails = error.detail;
            console.error("UPLOAD ERROR DETAILS:", errorDetails);
          }
        } catch (parseErr) {
          console.error("UPLOAD ERROR - Could not parse response:", parseErr);
          errorMessage = t("crewPortal.uploadFailedStatus", { status: response.status });
        }
        
        // Show user-friendly error toast
        toast.error(t("crewPortal.uploadFailedPrefix", { message: errorMessage }), {
          duration: 5000,
          description: errorDetails?.error_type || undefined,
        });
      }
    } catch (error) {
      // Network or other errors
      console.error("UPLOAD NETWORK ERROR:", error);
      toast.error(t("crewPortal.uploadFailedNetwork", { message: error.message || "Network error" }), {
        duration: 5000,
      });
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  // Handle document deletion
  const handleDelete = async () => {
    if (!deleteDialog.document) return;

    setDeleting(true);
    try {
      const token = localStorage.getItem("eventsflo_auth_token");
      const response = await fetch(
        `${API_URL}${CREW_ENDPOINTS.deleteDocument(deleteDialog.document.id)}`,
        {
          method: "DELETE",
          headers: { "Authorization": `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success(t("crewPortal.onboardingDocs.deleteSuccess"));
        // Remove from local state immediately
        setDocuments(prev => prev.filter(d => d.id !== deleteDialog.document.id));
        // Also notify parent
        if (onDocumentsChange) {
          onDocumentsChange(documents.filter(d => d.id !== deleteDialog.document.id));
        }
      } else {
        const error = await response.json().catch(() => ({}));
        toast.error(error.detail || "Failed to delete document");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(t("crewPortal.onboardingDocs.deleteFailed"));
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, document: null });
    }
  };

  // Get status icon and color
  const getStatusDisplay = (status) => {
    switch (status) {
      case "verified":
        return {
          icon: <CheckCircle className="h-4 w-4" />,
          label: "Verified",
          className: "text-green-600 border-green-600"
        };
      case "rejected":
        return {
          icon: <XCircle className="h-4 w-4" />,
          label: "Rejected",
          className: "text-red-600 border-red-600"
        };
      default:
        return {
          icon: <Clock className="h-4 w-4" />,
          label: "Pending",
          className: "text-amber-600 border-amber-600"
        };
    }
  };

  return (
    <Card className={className} data-testid="crew-documents-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t("crewPortal.onboardingDocs.title")}
        </CardTitle>
        <CardDescription>
          {t("crewPortal.onboardingDocs.subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Required Documents List */}
        {requiredDocuments.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("crewPortal.onboardingDocs.requiredLabel")}</Label>
            <div className="space-y-1">
              {requiredDocuments.map((doc, idx) => {
                const uploaded = documents.some(
                  d => d.document_type_code === doc.document_type_code ||
                       d.document_type === doc.document_type_code
                );
                return (
                  <div 
                    key={idx} 
                    className="flex items-center gap-2 text-sm"
                    data-testid={`required-doc-${doc.document_type_code}`}
                  >
                    {uploaded ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                    )}
                    <span className={uploaded ? "text-muted-foreground" : ""}>
                      {doc.document_type_name || doc.document_type_code}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upload Area */}
        <div className="space-y-2">
          <Label htmlFor="document-upload" className="text-sm font-medium">
            {t("crewPortal.onboardingDocs.uploadLabel")}
          </Label>
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
            <Input
              id="document-upload"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
              data-testid="document-upload-input"
            />
            <Label
              htmlFor="document-upload"
              className="cursor-pointer flex flex-col items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">{t("crewPortal.onboardingDocs.uploading")}</span>
                </>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {t("crewPortal.onboardingDocs.clickToUpload")}
                  </span>
                </>
              )}
            </Label>
          </div>
        </div>

        {/* Document List */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : documents.length > 0 ? (
          <div className="space-y-2">
            <h4 className="font-medium text-sm mb-2">
              Uploaded Documents ({documents.length}):
            </h4>
            {documents.map((doc) => {
              const status = getStatusDisplay(doc.status);
              return (
                <div
                  key={doc.id}
                  data-testid={`document-item-${doc.id}`}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                    <div className="min-w-0">
                      {/* EF-ENHANCE-DOCUMENT-PREVIEW-UX-ADMIN-AND-CREW-V1: Clickable filename for preview
                          EVENTSFLO-CREW-DOCUMENTS-FILE-MISSING-BADGE-AND-SAFE-VISIBILITY-V1:
                          When the backing file is missing from storage, disable preview and
                          show a red "File missing" badge. Metadata remains for audit. */}
                      <button
                        onClick={() => !doc.file_missing && setPreviewDoc(doc)}
                        disabled={!!doc.file_missing}
                        className={`text-sm font-medium text-left truncate block max-w-full ${
                          doc.file_missing
                            ? "text-muted-foreground cursor-not-allowed line-through opacity-70"
                            : "hover:underline hover:text-primary cursor-pointer"
                        }`}
                        title={
                          doc.file_missing
                            ? "File is missing from storage. Metadata remains for audit purposes."
                            : `Click to preview: ${doc.original_filename || doc.filename || "Document"}`
                        }
                        data-testid={`preview-document-${doc.id}`}
                      >
                        {doc.original_filename || doc.filename || "Document"}
                      </button>
                      <p className="text-xs text-muted-foreground truncate">
                        {doc.document_type_code || doc.document_type || "Document"}
                        {doc.created_at && ` • ${new Date(doc.created_at).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* EVENTSFLO-CREW-DOCUMENTS-FILE-MISSING-BADGE-AND-SAFE-VISIBILITY-V1:
                        Red "File missing" badge beside status when backing file is gone. */}
                    {doc.file_missing && (
                      <Badge
                        variant="destructive"
                        data-testid={`file-missing-badge-${doc.id}`}
                        title="File is missing from storage. Metadata remains for audit purposes."
                        className="gap-1"
                      >
                        <AlertCircle className="h-3 w-3" />
                        File missing
                      </Badge>
                    )}
                    {/* Preview button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed"
                      onClick={() => setPreviewDoc(doc)}
                      disabled={!!doc.file_missing}
                      title={
                        doc.file_missing
                          ? "File is missing from storage. Metadata remains for audit purposes."
                          : t("onboardingDocs.previewTooltip")
                      }
                      data-testid={`preview-btn-${doc.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Badge variant="outline" className={status.className}>
                      {status.label}
                    </Badge>
                    {doc.status !== "verified" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => setDeleteDialog({ open: true, document: doc })}
                        data-testid={`delete-document-${doc.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t("crewPortal.onboardingDocs.emptyText")}</p>
          </div>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog 
        open={deleteDialog.open} 
        onOpenChange={(open) => !deleting && setDeleteDialog({ open, document: open ? deleteDialog.document : null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("crewPortal.onboardingDocs.deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("crewPortal.onboardingDocs.deleteDialog.description", {
                name: deleteDialog.document?.original_filename || deleteDialog.document?.filename || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("crewPortal.onboardingDocs.deleteDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-document"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("crewPortal.onboardingDocs.deleteDialog.deleting")}
                </>
              ) : (
                t("crewPortal.onboardingDocs.deleteDialog.delete")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* EF-ENHANCE-DOCUMENT-PREVIEW-UX-ADMIN-AND-CREW-V1: Document Preview Modal */}
      <DocumentPreviewModal
        document={previewDoc}
        open={!!previewDoc}
        onOpenChange={(open) => !open && setPreviewDoc(null)}
      />
    </Card>
  );
}

export default CrewDocumentsPanel;
